import { type ReactNode } from 'react';
import { cn } from '@/lib/cn';
import { Checkbox } from '@/components/ui/checkbox';

export interface Column<T> {
  key: string;
  header: ReactNode;
  cell: (row: T) => ReactNode;
  className?: string;
  headerClassName?: string;
  width?: string;
}

/** Opt-in multi-select. When provided, a leading checkbox column is rendered. */
export interface DataTableSelection {
  selectedIds: Set<string>;
  onToggleRow: (id: string) => void;
  /** Toggle every currently-visible row (select-all header checkbox). */
  onToggleAll: () => void;
}

interface DataTableProps<T> {
  rows: T[];
  columns: Column<T>[];
  rowKey: (row: T) => string;
  onRowClick?: (row: T) => void;
  empty?: ReactNode;
  className?: string;
  selection?: DataTableSelection;
}

export function DataTable<T>({
  rows,
  columns,
  rowKey,
  onRowClick,
  empty,
  className,
  selection,
}: DataTableProps<T>) {
  if (rows.length === 0 && empty) {
    return <>{empty}</>;
  }

  const ids = rows.map(rowKey);
  const allSelected = selection != null && ids.length > 0 && ids.every((id) => selection.selectedIds.has(id));
  const someSelected = selection != null && ids.some((id) => selection.selectedIds.has(id));

  return (
    <div className={cn('overflow-hidden rounded-lg border border-border bg-card', className)}>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="border-b border-border bg-muted/30">
            <tr className="text-left">
              {selection && (
                <th className="w-[44px] px-4 py-2.5">
                  <Checkbox
                    aria-label="Select all rows"
                    checked={allSelected}
                    indeterminate={someSelected && !allSelected}
                    onChange={selection.onToggleAll}
                  />
                </th>
              )}
              {columns.map((col) => (
                <th
                  key={col.key}
                  className={cn(
                    'px-4 py-2.5 text-xs font-medium uppercase tracking-wider text-muted-foreground',
                    col.headerClassName,
                  )}
                  style={col.width ? { width: col.width } : undefined}
                >
                  {col.header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {rows.map((row) => {
              const id = rowKey(row);
              return (
                <tr
                  key={id}
                  onClick={onRowClick ? () => onRowClick(row) : undefined}
                  className={cn(
                    'transition-colors',
                    onRowClick && 'cursor-pointer hover:bg-muted/40',
                    selection?.selectedIds.has(id) && 'bg-primary/5',
                  )}
                >
                  {selection && (
                    <td
                      className="w-[44px] px-4 py-3 align-middle"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Checkbox
                        aria-label="Select row"
                        checked={selection.selectedIds.has(id)}
                        onChange={() => selection.onToggleRow(id)}
                      />
                    </td>
                  )}
                  {columns.map((col) => (
                    <td
                      key={col.key}
                      className={cn('px-4 py-3 align-middle text-foreground', col.className)}
                    >
                      {col.cell(row)}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
